<template>
  <div>
    <AlertComponent />
    <LoadingOverlay />
    <LoginComponent v-if="!isAuthenticated" :login="handleLogin" />
    <div v-else class="app-container">
      <MenuComponent :isCollapsed="isCollapsed" :configMenu="configMenu" :username="username"
        @toggleSidebar="toggleSidebar" @changeView="currentView = $event" @logout="logout" />

      <KeepAlive class="main-content" :style="styleObject">
        <component :is="currentView" @changeView="currentView = $event"></component>
      </KeepAlive>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, onBeforeUnmount, defineAsyncComponent } from 'vue';
import { useStore } from 'vuex';
import { apiRequest } from './api';
import { Module, Action, MenuItem } from './interfaces';

export default defineComponent({
  name: 'App',
  components: {
    LoginComponent: defineAsyncComponent(() => import('./components/LoginComponent.vue')),
    MenuComponent: defineAsyncComponent(() => import('./components/Menu/MenuComponent.vue')),
    UserComponent: defineAsyncComponent(() => import('./components/User/UserComponent.vue')),
    StoreComponent: defineAsyncComponent(() => import('./components/Store/StoreComponent.vue')),
    ModuleComponent: defineAsyncComponent(() => import('./components/Module/ModuleComponent.vue')),
    ActionComponent: defineAsyncComponent(() => import('./components/Action/ActionComponent.vue')),
    StatusComponent: defineAsyncComponent(() => import('./components/StatusComponent.vue')),
    AlertComponent: defineAsyncComponent(() => import('@/components/AlertComponent.vue')),
    ProductsComponent: defineAsyncComponent(() => import('./components/Products/ProductsComponent.vue')),
    OrderComponent: defineAsyncComponent(() => import('./components/Order/OrderComponent.vue')),
    LoadingOverlay: defineAsyncComponent(() => import('@/components/LoadingOverlay.vue')),
    DetailsComponent: defineAsyncComponent(() => import('./components/Details/DetailsComponent.vue')),
    HolidayComponent: defineAsyncComponent(() => import('./components/Holiday/HolidayComponent.vue')),
    HomeComponent: defineAsyncComponent(() => import('./components/HomePage/HomePageComponent.vue')),
    OrderCotationComponent: defineAsyncComponent(() => import('./components/Order-admin/OrderCotationComponent.vue')),
  },
  setup() {
    const store = useStore();
    const currentView = ref('HomeComponent');
    const isCollapsed = ref(true);
    const isAuthenticated = ref(false);
    const username = computed(() => store.state.user ? store.state.user.name : 'Admin');
    const user = computed(() => store.state.user);
    const configMenuDefault: MenuItem[] = [
      { name: 'Página Principal', component: 'HomeComponent', icon: 'bi-house', actionRoute: '/home-page:get' },
      { name: 'Visão Geral', component: 'DetailsComponent', icon: 'bi-graph-up', actionRoute: '/store-details:get' },
      { name: 'Usuários', component: 'UserComponent', icon: 'bi-person', actionRoute: '/users:get' },
      { name: 'Pedidos', component: 'OrderComponent', icon: 'bi-cart', actionRoute: '/orders:get' },
      { name: 'Cotações(admin)', component: 'OrderCotationComponent', icon: 'bi-currency-exchange', actionRoute: '/order:delete' },
      { name: 'Produtos', component: 'ProductsComponent', icon: 'bi-boxes', actionRoute: '/search-products:get' },
      { name: 'Lojas', component: 'StoreComponent', icon: 'bi-shop', actionRoute: '/stores:get' },
      { name: 'Feriados', component: 'HolidayComponent', icon: 'bi-calendar4', actionRoute: '/holiday:get' },
      { name: 'Perfis de acesso', component: 'ModuleComponent', icon: 'bi-person-badge', actionRoute: '/modules:get' },
      { name: 'Permissões', component: 'ActionComponent', icon: 'bi-play', actionRoute: '/actions:get' },
      { name: 'Status', component: 'StatusComponent', icon: 'bi-info-circle', actionRoute: '/status:get' },
    ];

    const configMenu = ref<MenuItem[]>(configMenuDefault);
    const isSidebarOpen = ref(false);
    const styleObject = computed(() => ({ marginLeft: isSidebarOpen.value ? '250px' : '100px' }));

    const menuRender = () => {
      try {
        configMenu.value = configMenuDefault.filter((item: MenuItem) => {
          return user?.value?.modules.some((module: Module) => {
            if (item.actionRoute === '/actions:get') {
              return module.name === 'Admin';
            }

            if (item.component === 'OrderCotationComponent') {
              return module.name === 'Admin';
            }
            
            return module.actions.some((action: Action) => {
              return action.route === item.actionRoute;
            });
          });
        });
      } catch (error) {
        console.error('Error ao renderizar menu: ', error)
      }
    }

    const handleLogin = async (login: string, password: string) => {
      try {
        await store.dispatch('login', { login, password });
        if (store.state.isAuthenticated) {
          isAuthenticated.value = true;
          currentView.value = 'HomeComponent';
          // define menu itens based on user permissions
          menuRender();
        }
      } catch (error) {
        store.dispatch('alert', { message: `Falha no login: ${error}`, type: 'error' });
      }
    };

    const logout = async () => {
      store.dispatch('logout');
      isAuthenticated.value = false;
      await apiRequest({ method: 'GET', url: '/logout' });
    };

    const toggleSidebar = () => {
      isCollapsed.value = !isCollapsed.value;
      isSidebarOpen.value = !isSidebarOpen.value;
    };

    const checkSession = async () => {
      try {
        const checkCookie = await apiRequest({ method: 'GET', url: '/back/check' });
        if (checkCookie.authenticated) {
          store.dispatch('setAuthenticated', true);
          isAuthenticated.value = true;
        } else {
          throw new Error('Não autenticado');
        }
      } catch (error) {
        isAuthenticated.value = false;
        store.dispatch('alert', { message: 'Sessão expirada. Por favor, faça login novamente.', type: 'info' });
      }
    };

    let sessionCheckInterval: number;
    onMounted(async () => {
      await checkSession();
      sessionCheckInterval = setInterval(checkSession, 60000);
      menuRender()
    });

    onBeforeUnmount(() => {
      clearInterval(sessionCheckInterval);
    });

    return {
      isAuthenticated,
      currentView,
      handleLogin,
      logout,
      isCollapsed,
      toggleSidebar,
      username,
      configMenu,
      isSidebarOpen,
      styleObject
    };
  }
});
</script>

<style scoped>
:root {
  --bs-nav-link-hover-color: #626262;
  --bs-link-hover-color: #626262;
}

html,
body,
#app {
  height: 100%;
  margin: 0;
  overflow: hidden;
}

.app-container {
  display: flex;
  width: 100%;
  height: 100%;
}

.btn-primary {
  background-color: #ffffff;
  border-color: #000000;
  color: #000000;
  border-radius: 4px;
}

.btn-primary:hover {
  background-color: #414141;
  color: #ffffff;
  border: black;
}

.btn-primary:active {
  background-color: #000000;
  color: #ffffff;
  border: black;
}

.btn-secondary {
  background-color: #464545;
  border-color: #464545;
  color: #ffffff;
  border-radius: 4px;
}

.btn-danger {
  border-radius: 4px;
}

.btn-secondary:hover {
  background-color: #000000;
  color: #ffffff;
  border: rgb(243, 241, 241);
}

.btn-secondary:active {
  background-color: #000000;
  color: #ffffff;
  border: black;
}

/* fix it to select link */

.entity-list h2 {
  margin-bottom: 20px;
}

.entity-list ul {
  list-style: none;
  padding: 0;
}

.entity-list li {
  padding: 10px;
  border-bottom: 1px solid #ddd;
}

.entity-list li:last-child {
  border-bottom: none;
}

.modal {
  display: block;
  background: rgba(0, 0, 0, 0.5);
}

.modal-dialog {
  max-width: 60vw;
  margin: 1.75rem auto;
}

.modal-content {
  background-color: #fff;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 0.3rem;
  box-shadow: 0 3px 9px rgba(0, 0, 0, 0.5);
  outline: 0;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #dee2e6;
}

.modal-body {
  position: relative;
  padding: 1rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  padding: 1rem;
  border-top: 1px solid #dee2e6;
}
</style>